import {
  AddEquation,
  CustomBlending,
  DoubleSide,
  NodeMaterial,
  OneFactor,
  OneMinusSrcAlphaFactor,
  type Texture,
  Vector2,
  Vector4,
} from "three/webgpu";
import * as TSL from "three/tsl";
import type { GpuContext } from "../engine/GpuContext.js";
import { InstanceRuns } from "../engine/InstanceRuns.js";
import type { DrawableTexture } from "../engine/texture.js";
import type { Mat2D, Rect2D } from "./math2d.js";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type N = any;
const { attribute, mix, texture: sample, uniform, varying, vec2, vec4 } = TSL as N;

/** 4 x vec4 - center and half-size, rotation, uv, tint. Must match `ATTRIBUTES`. */
export const FLOATS_PER_SPRITE = 16;
const ATTRIBUTES = ["sPlace", "sTurn", "sUv", "sTint"] as const;

const WHITE = { r: 1, g: 1, b: 1, a: 1 } as const;

export interface SpriteQuad {
  /** Center in world space. */
  x: number;
  y: number;
  width: number;
  height: number;
  /** Radians. */
  rotation?: number;
  /** Sub-rectangle in texture pixels; omit for the whole texture. */
  frame?: Rect2D | null;
  tint?: { r: number; g: number; b: number; a: number } | null;
  opacity?: number;
}

interface Camera {
  /** a, b, c, d of the world -> view affine transform. */
  matrix: { value: Vector4 };
  /** tx, ty. */
  offset: { value: Vector2 };
  /** Target size in pixels. */
  viewport: { value: Vector2 };
}

/**
 * Textured, rotated, tinted sprites. A material of its own rather than an extension of the UI's quad
 * material, deliberately: the UI's is a rounded-rectangle distance field with no rotation, and a sprite is a
 * much simpler thing - no border, no corner radius, no distance field.
 */
function spriteMaterial(map: Texture, camera: Camera): NodeMaterial {
  const place: N = attribute("sPlace", "vec4");
  const turn: N = attribute("sTurn", "vec4");
  const uvRect: N = attribute("sUv", "vec4");
  const tint: N = attribute("sTint", "vec4");
  const corner: N = attribute("position", "vec3").xy;

  // Rotate in the sprite's own space, then place it in the world, then through the camera.
  const offset = corner.mul(2.0).sub(1.0).mul(place.zw);
  const rotated = vec2(offset.x.mul(turn.x).sub(offset.y.mul(turn.y)), offset.x.mul(turn.y).add(offset.y.mul(turn.x)));
  const world = place.xy.add(rotated);
  const m: N = camera.matrix;
  const view = vec2(
    m.x.mul(world.x).add(m.z.mul(world.y)).add((camera.offset as N).x),
    m.y.mul(world.x).add(m.w.mul(world.y)).add((camera.offset as N).y),
  );
  // Pixels -> clip space; y grows downward in pixels, upward in clip space.
  const ndc = view.div(camera.viewport).mul(2.0).sub(1.0);

  const uv = varying(mix(uvRect.xy, uvRect.zw, corner), "vSpriteUv");
  const texel = sample(map, uv);
  // Straight alpha in, premultiplied out - the target is drawn by the UI, which expects premultiplied.
  const a = texel.a.mul(tint.a);

  const material = new NodeMaterial();
  material.name = "webwin.sprite";
  material.vertexNode = vec4(ndc.x, ndc.y.negate(), 0.0, 1.0);
  material.fragmentNode = vec4(texel.rgb.mul(tint.rgb).mul(a), a);
  material.transparent = true;
  material.depthTest = false;
  material.depthWrite = false;
  material.side = DoubleSide;
  material.blending = CustomBlending;
  material.blendEquation = material.blendEquationAlpha = AddEquation;
  material.blendSrc = material.blendSrcAlpha = OneFactor;
  material.blendDst = material.blendDstAlpha = OneMinusSrcAlphaFactor;
  return material;
}

/**
 * Accumulates a frame's sprites and draws them in as few calls as it can: a new run only when the texture
 * changes, because sprites are overwhelmingly the same handful of atlases.
 *
 * Order is preserved exactly. A 2D renderer has no depth buffer, so what was submitted last is what
 * appears on top, and quietly regrouping by texture to save a draw call would reorder the scene.
 */
export class SpriteBatch {
  #runs: InstanceRuns;
  #camera: Camera = {
    matrix: uniform(new Vector4(1, 0, 0, 1)),
    offset: uniform(new Vector2()),
    viewport: uniform(new Vector2(1, 1)),
  };
  #materials = new WeakMap<Texture, NodeMaterial>();

  constructor(gpu: GpuContext) {
    this.#runs = new InstanceRuns(gpu, ATTRIBUTES, (texture) => {
      let material = this.#materials.get(texture);
      if (!material) this.#materials.set(texture, (material = spriteMaterial(texture, this.#camera)));
      return material;
    });
  }

  get spriteCount(): number {
    return this.#runs.instanceCount;
  }

  get drawCallCount(): number {
    return this.#runs.drawCallCount;
  }

  /** Start a frame. `view` is world -> view pixels; `width`/`height` the target. */
  begin(view: Mat2D, width: number, height: number): void {
    this.#runs.reset();
    this.#camera.matrix.value.set(view.a, view.b, view.c, view.d);
    this.#camera.offset.value.set(view.tx, view.ty);
    this.#camera.viewport.value.set(width, height);
  }

  /** Append one sprite. */
  push(texture: DrawableTexture, quad: SpriteQuad): void {
    if (quad.width === 0 || quad.height === 0) return;
    const { data: d, index } = this.#runs.reserve(texture.texture);
    let i = index;
    const frame = quad.frame;
    const tint = quad.tint ?? WHITE;
    const rotation = quad.rotation ?? 0;

    d[i++] = quad.x;
    d[i++] = quad.y;
    d[i++] = quad.width / 2;
    d[i++] = quad.height / 2;

    // One sincos here rather than one per vertex in the shader.
    d[i++] = Math.cos(rotation);
    d[i++] = Math.sin(rotation);
    d[i++] = 0;
    d[i++] = 0;

    if (frame) {
      d[i++] = frame.x / texture.width;
      d[i++] = frame.y / texture.height;
      d[i++] = (frame.x + frame.width) / texture.width;
      d[i++] = (frame.y + frame.height) / texture.height;
    } else {
      d[i++] = 0;
      d[i++] = 0;
      d[i++] = 1;
      d[i++] = 1;
    }

    d[i++] = tint.r;
    d[i++] = tint.g;
    d[i++] = tint.b;
    d[i] = tint.a * (quad.opacity ?? 1);
  }

  /** Upload the frame and draw it into whatever the renderer is pointed at. */
  flush(): void {
    this.#runs.flush();
  }

  dispose(): void {
    this.#runs.dispose();
  }
}
